import { Col, Row } from "react-bootstrap";
import styled from "styled-components";
import { JSONObjectSchema, SchemaTypes, BlueprintOperationType } from "../../../models/Blueprints";
import BlueprintValidationEditorObjectFieldSet from "./BlueprintValidationEditorObjectFieldSet";
import { BlueprintValidationActions } from "./BlueprintValidationEditorUtils";
import CenteredVerticalLineCol from "./CenteredVerticalLineCol";
import { palette } from "../../../styles/theme/colors";
import { List } from "lucide-react";
import DeprecatedH4 from "../../deprecated/DeprecatedH4";
import { Card } from "@merge-api/merge-javascript-shared";

const IconContainer = styled.div.attrs({
  className: "avatar-sm avatar-title rounded-circle",
})`
  margin: 24px 25px;
  color: ${palette.black};
`;

const Title = styled(DeprecatedH4)`
  font-size: 20px;
  line-height: 28px;
  color: ${palette.black};
  border-bottom: 0;
  -webkit-text-decoration-skip-ink: none;
  text-decoration-skip-ink: none;
`;

const CardHeaderRow = styled(Row).attrs({ className: "flex-nowrap ml-0 mr-0" })`
  border-bottom: 1px solid ${palette.border};
`;

const MaybeCenteredVerticalLineCol = styled(CenteredVerticalLineCol)<{ $showLine: boolean }>`
  background-size: 1px 50%;
  background-position: center 44px;
  padding-left: 0;
  padding-right: 0;
  flex: 0 0 auto;
  width: auto;
  max-width: 100%;
`;

type Props = {
  schema: JSONObjectSchema;
  schemaType: SchemaTypes;
  validationActions: BlueprintValidationActions;
  operationType: undefined | string;
};

const BlueprintValidationEditorCard = ({
  schema,
  schemaType,
  validationActions,
  operationType,
}: Props) => {
  const isFunctionalBP = operationType === BlueprintOperationType.FUNCTIONAL;
  const isBulkCreate = operationType === BlueprintOperationType.BULK_CREATE;

  // For BULK_CREATE, the schema has batch_items at the top level instead of nested under model
  const schemaKey = isBulkCreate ? "batch_items" : "model";
  const schemaProperty = schema.properties?.[schemaKey];

  // For BULK_CREATE, batch_items is an array, so we need to get its items schema
  let objectSchema: JSONObjectSchema;
  let objectTraversalPath: string[];

  if (isBulkCreate && schemaProperty && schemaProperty.type === "array") {
    // For batch_items array, we want to edit the items schema (the object inside the array)
    objectSchema = (schemaProperty as any).items as JSONObjectSchema;
    objectTraversalPath = ["properties", schemaKey, "items"];
  } else {
    objectSchema = schemaProperty
      ? (schemaProperty as JSONObjectSchema)
      : (schema as JSONObjectSchema);
    objectTraversalPath = isFunctionalBP || !schemaProperty ? [] : ["properties", schemaKey];
  }

  const objTitle = !isFunctionalBP
    ? "Model"
    : schemaType === SchemaTypes.parameter_schema
    ? "Parameters"
    : "Return values";
  return (
    <Card className="flex flex-col w-full">
      <CardHeaderRow>
        <MaybeCenteredVerticalLineCol>
          <IconContainer>
            <List size={"13px"} />
          </IconContainer>
        </MaybeCenteredVerticalLineCol>
        <Col className="my-auto pr-9">
          <Row>
            <Col className="d-flex pl-0 my-auto">
              <Title className="mb-0">{objTitle}</Title>
            </Col>
          </Row>
        </Col>
      </CardHeaderRow>
      <BlueprintValidationEditorObjectFieldSet
        fieldKey={schemaKey}
        level={1}
        objectJSONSchema={objectSchema}
        validationActions={validationActions}
        traversalPath={objectTraversalPath}
        operationType={operationType}
      />
    </Card>
  );
};

export default BlueprintValidationEditorCard;
